// @flow
/** @jsx jsx */

import { jsx } from '@emotion/core';
import styled from '@emotion/styled';
import * as React from 'react';
import Button from 'react-bootstrap/lib/Button';
import Modal from 'react-bootstrap/lib/Modal';
import Glyphicon from 'react-bootstrap/lib/Glyphicon';
import ControlLabel from 'react-bootstrap/lib/ControlLabel';
import FormControl from 'react-bootstrap/lib/FormControl';
import { Query } from 'react-apollo';

import type { CommonActionProps } from './types.actions';
import type { GridItem } from '../../types';
import ToggleController from '../../controllers/ToggleController';
import ButtonAction from './ButtonAction';
import FormRow from '../FormRow';
import DescriptionCell from '../DescriptionCell';
import inventoryNotesQuery from '../../graphql/queries/inventoryNotes.query';
import Loading from '../Loading';
import { DesktopView, MobileView } from '../../config/mediaQuery';

type Props = {
  item: GridItem,
};

const Col = styled('div')`
  flex: 1;
`;

const Spacer = styled('div')`
  width: 20px;
`;

const RowDetails = (props: { field: string, title: string }) => (
  <Col>
    <FormRow
      css={{
        flexDirection: 'column',
      }}
    >
      <Col>
        <ControlLabel>{props.title}</ControlLabel>
      </Col>
      <DescriptionCell>
        <FormControl
          componentClass="textarea"
          value={props.field || ''}
          readOnly
          css={{
            minHeight: '100px',
          }}
        />
      </DescriptionCell>
    </FormRow>
  </Col>
);

const InventoryNotesAction = (props: { item: GridItem }) => {
  const { item } = props;

  if (!item) return null;

  // Only send Code to match InventoryRowInput and avoid GraphQL 400 errors.
  const selectedRow = { Code: item.Code };

  return (
    <Query
      query={inventoryNotesQuery}
      variables={{
        selectedRow,
        moduleName: 'inventoryNotes',
      }}
    >
      {({ data, loading, error }: any) => {
        if (loading) {
          return <Loading />;
        }

        if (error) {
          return <div style={{ padding: '20px', color: '#888' }}>Unable to load notes. Please try again.</div>;
        }

        const notes = (data && data.inventoryNotes) || {};
        const NotesVar = notes.NotesVar || '';
        const NotesTast = notes.NotesTast || '';
        const NotesVinif = notes.NotesVinif || '';
        const NotesVine = notes.NotesVine || '';
        const NotesAge = notes.NotesAge || '';
        const NotesProd = notes.NotesProd || '';
        const NotesSoil = notes.NotesSoil || '';
        const NotesOrien = notes.NotesOrien || '';
        const NotesViti = notes.NotesViti || '';

        const notesVar = <RowDetails field={NotesVar} title="Varietal Notes" />;
        const notesVine = <RowDetails field={NotesVine} title="Vineyard Notes" />;
        const notesProd = <RowDetails field={NotesProd} title="Production Notes" />;
        const notesSoil = <RowDetails field={NotesSoil} title="Soil Notes" />;
        const notesTaste = <RowDetails field={NotesTast} title="Tasting Notes" />;
        const notesVinif = <RowDetails field={NotesVinif} title="Vinification Notes" />;
        const notesAging = <RowDetails field={NotesAge} title="Aging Notes" />;
        const notesOrientation = <RowDetails field={NotesOrien} title="Orientation Notes" />;
        const notesViti = <RowDetails field={NotesViti} title="Viticulture Notes" />;

        return (
          <div>
            <DesktopView>
              <FormRow>
                <ControlLabel>
                  {item.Code} {item.Description}
                </ControlLabel>
              </FormRow>
              <FormRow>
                {notesVar}
                <Spacer />
                {notesTaste}
              </FormRow>
              <FormRow>
                {notesVine}
                <Spacer />
                {notesVinif}
              </FormRow>
              <FormRow>
                {notesProd}
                <Spacer />
                {notesAging}
              </FormRow>
              <FormRow>
                {notesSoil}
                <Spacer />
                {notesOrientation}
              </FormRow>
              <FormRow>
                {notesViti}
              </FormRow>
            </DesktopView>
            <MobileView>
              {notesVar}
              {notesVine}
              {notesProd}
              {notesSoil}
              {notesTaste}
              {notesVinif}
              {notesAging}
              {notesOrientation}
              {notesViti}
            </MobileView>
          </div>
        );
      }}
    </Query>
  );
};

const AdditionalInfoAction = (props: CommonActionProps & Props) => (
  <ToggleController>
    {({ onToggleVisibility, visible }) => (
      <React.Fragment>
        <DesktopView>
          <ButtonAction
            bsSize={props.size}
            disabled={props.disabled}
            onClick={onToggleVisibility}
          >
            <Glyphicon glyph="list-alt" /> Notes
          </ButtonAction>
        </DesktopView>
        <MobileView>
          <button disabled={props.disabled} onClick={onToggleVisibility}>
            <img src='assets/notes.svg' alt='notes icon'/> Notes
          </button>
        </MobileView>
        <MobileView>
          {(isMobile) => (
            <Modal show={visible} onHide={onToggleVisibility} dialogClassName={isMobile ? 'inventory-notes-modal' : ''}>
              <Modal.Header closeButton={!isMobile}>
                <Modal.Title>Inventory Notes</Modal.Title>
                {isMobile && props.item && <p>{`${props.item.Code} ${props.item.Description}`}</p>}
              </Modal.Header>
              <Modal.Body>
                <InventoryNotesAction item={props.item} />
              </Modal.Body>
              <Modal.Footer>
                <DesktopView>
                  <Button onClick={onToggleVisibility}>Close</Button>
                </DesktopView>
                <MobileView>
                  <div className='button-single-modal'>
                    <button
                      onClick={onToggleVisibility}
                    >
                      Close
                    </button>
                  </div>
                </MobileView>
              </Modal.Footer>
            </Modal>
          )}

        </MobileView>
      </React.Fragment>
    )}
  </ToggleController>
);

AdditionalInfoAction.defaultProps = {
  size: 'xsmall',
};

export default AdditionalInfoAction;
